import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getBroadcastCalendar } from '@/services/api/broadcast';
import type { BroadcastGroupSummary } from '@/types/broadcast';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, Loader2, AlertTriangle, Send, Users } from 'lucide-react';
import BroadcastDayModal from './BroadcastDayModal';
import {
    BRT_TZ,
    diaBRT,
    inicioDaGrade,
    fimDaGrade,
    limiteUtcDoDiaBRT,
    mesPorExtenso,
} from '../utils/calendarUtils';

interface BroadcastCalendarProps {
    onDuplicate: (group: BroadcastGroupSummary) => void;
    onCancel: (group: BroadcastGroupSummary) => void;
    onView: (group: BroadcastGroupSummary) => void;
    /** Dono filtrado pelo admin; sem valor, o escopo de sempre. */
    userId?: number;
}

const DIAS_SEMANA = ['DOM', 'SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SÁB'];

/*
 * O mês vem numa requisição só, de propósito.
 *
 * Chegou a existir uma versão em duas camadas: a grade pedia totais por dia
 * (`/calendar/summary`, 0,63 s) e o dia era buscado no clique (0,30 s). Ela
 * ganha de quem abre o mês e não clica, e ganha muito em quem navega entre
 * meses. Mas perde no uso real desta tela, que é abrir o mês e entrar em
 * VÁRIOS dias seguidos — aí cada clique paga de novo, contra 1,13 s uma vez.
 *
 * Medido em 23/09, mês e meio de dados:
 *   mês inteiro ......... 1,07–1,19 s / 142 KB, dias instantâneos depois
 *   duas camadas ........ 0,63 s + 0,30 s por dia aberto
 *
 * A rota `/calendar/summary` continua no backend; se o padrão de uso mudar,
 * voltar é trocar o `queryFn` abaixo.
 */

/** Data que posiciona o grupo no calendário: agendamento, ou criação se foi imediato. */
export const quandoAconteceu = (g: BroadcastGroupSummary): string | undefined =>
    g.scheduled_at ?? g.created_at ?? undefined;

export default function BroadcastCalendar({ onDuplicate, onCancel, onView, userId }: BroadcastCalendarProps) {
    const hoje = new Date();
    const [mesVisivel, setMesVisivel] = useState(() => new Date(hoje.getFullYear(), hoje.getMonth(), 1));
    const [diaAberto, setDiaAberto] = useState<string | null>(null);

    const grade = useMemo(() => {
        const inicio = inicioDaGrade(mesVisivel);
        const fim = fimDaGrade(mesVisivel);
        const dias: Date[] = [];
        for (let d = new Date(inicio); d <= fim; d.setDate(d.getDate() + 1)) {
            dias.push(new Date(d));
        }
        return { inicio, fim, dias };
    }, [mesVisivel]);

    const { data, isLoading, error } = useQuery({
        queryKey: ['broadcast-calendar', grade.inicio.toISOString(), grade.fim.toISOString(), userId ?? null],
        queryFn: () => getBroadcastCalendar(
            limiteUtcDoDiaBRT(grade.inicio, 'inicio'),
            limiteUtcDoDiaBRT(grade.fim, 'fim'),
            userId,
        ),
        staleTime: 1000 * 60,
        refetchOnWindowFocus: false,
    });

    // Um balde por dia BRT. A chave vem do fuso de Brasília, e não do fuso do
    // navegador: é o mesmo que os cards exibem, então o dia do calendário e a
    // hora do card nunca discordam, mesmo com o usuário fora do país.
    const porDia = useMemo(() => {
        const mapa = new Map<string, BroadcastGroupSummary[]>();
        for (const g of data?.items ?? []) {
            const quando = quandoAconteceu(g);
            if (!quando) continue;
            const chave = diaBRT(quando);
            if (!chave) continue;
            const lista = mapa.get(chave);
            if (lista) lista.push(g); else mapa.set(chave, [g]);
        }
        for (const lista of mapa.values()) {
            lista.sort((a, b) => (quandoAconteceu(a) ?? '').localeCompare(quandoAconteceu(b) ?? ''));
        }
        return mapa;
    }, [data]);

    const resumo = useMemo(() => {
        const itens = data?.items ?? [];
        const doMes = itens.filter(g => {
            const chave = diaBRT(quandoAconteceu(g));
            return chave?.slice(0, 7) === `${mesVisivel.getFullYear()}-${String(mesVisivel.getMonth() + 1).padStart(2, '0')}`;
        });
        return {
            grupos: doMes.length,
            agendados: doMes.filter(g => g.status === 'pending').length,
            emAndamento: doMes.filter(g => g.status === 'sending' || g.status === 'processing').length,
            leads: doMes.reduce((s, g) => s + (g.total_leads ?? 0), 0),
            enviados: doMes.reduce((s, g) => s + (g.total_sent ?? 0), 0),
        };
    }, [data, mesVisivel]);

    const chaveHoje = diaBRT(new Date().toISOString());
    const mesAtual = mesVisivel.getMonth();

    const andarMes = (passo: number) =>
        setMesVisivel(m => new Date(m.getFullYear(), m.getMonth() + passo, 1));

    const gruposDoDiaAberto = diaAberto ? (porDia.get(diaAberto) ?? []) : [];

    return (
        <div className="space-y-4">
            <div className="rounded-2xl border border-gray-100 bg-white p-6">
                <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
                    <div>
                        <h3 className="text-sm font-semibold text-gray-900">Calendário</h3>
                        <p className="text-xs text-gray-500">
                            Por data de agendamento, no horário de Brasília.
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" className="rounded-xl" onClick={() => andarMes(-1)} aria-label="Mês anterior">
                            <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <span className="min-w-[150px] text-center text-sm font-semibold text-gray-900">
                            {mesPorExtenso(mesVisivel)}
                        </span>
                        <Button variant="outline" size="sm" className="rounded-xl" onClick={() => andarMes(1)} aria-label="Próximo mês">
                            <ChevronRight className="h-4 w-4" />
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            className="rounded-xl"
                            onClick={() => setMesVisivel(new Date(hoje.getFullYear(), hoje.getMonth(), 1))}
                        >
                            Hoje
                        </Button>
                    </div>
                </div>

                <div className="mb-5 grid grid-cols-2 gap-4 md:grid-cols-4">
                    <Indicador rotulo="Grupos no mês" valor={resumo.grupos.toLocaleString('pt-BR')}
                        detalhe={`${resumo.agendados} agendados · ${resumo.emAndamento} em andamento`} />
                    <Indicador rotulo="Leads planejados" valor={resumo.leads.toLocaleString('pt-BR')} icone={<Users className="h-3.5 w-3.5" />} />
                    <Indicador rotulo="Enviados" valor={resumo.enviados.toLocaleString('pt-BR')} icone={<Send className="h-3.5 w-3.5" />} />
                    <Indicador
                        rotulo="Alcance"
                        valor={resumo.leads > 0 ? `${Math.round((resumo.enviados / resumo.leads) * 100)}%` : '—'}
                        detalhe="enviados / planejados"
                    />
                </div>

                {data?.truncated && (
                    <div className="mb-4 flex items-start gap-2 rounded-xl bg-amber-50 px-3 py-2 text-xs text-amber-800">
                        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                        <span>
                            Este mês tem mais grupos do que o calendário carrega de uma vez.
                            Os dias podem estar incompletos — use a aba Execuções com o filtro de data.
                        </span>
                    </div>
                )}

                {error ? (
                    <div className="rounded-xl bg-red-50 px-4 py-6 text-center text-sm text-red-700">
                        Não consegui carregar o calendário.
                    </div>
                ) : (
                    <div className="relative">
                        {isLoading && (
                            <div className="absolute inset-0 z-10 flex items-center justify-center rounded-xl bg-white/70">
                                <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                            </div>
                        )}

                        <div className="grid grid-cols-7 gap-px rounded-xl bg-gray-100 p-px">
                            {DIAS_SEMANA.map(d => (
                                <div key={d} className="bg-gray-50 py-2 text-center text-[11px] font-semibold tracking-wider text-gray-400">
                                    {d}
                                </div>
                            ))}

                            {grade.dias.map(dia => {
                                const chave = `${dia.getFullYear()}-${String(dia.getMonth() + 1).padStart(2, '0')}-${String(dia.getDate()).padStart(2, '0')}`;
                                const grupos = porDia.get(chave) ?? [];
                                const doMes = dia.getMonth() === mesAtual;
                                const eHoje = chave === chaveHoje;
                                const leads = grupos.reduce((s, g) => s + (g.total_leads ?? 0), 0);

                                return (
                                    <button
                                        key={chave}
                                        type="button"
                                        onClick={() => grupos.length && setDiaAberto(chave)}
                                        disabled={!grupos.length}
                                        className={`min-h-[104px] bg-white p-2 text-left align-top transition-colors ${doMes ? '' : 'bg-gray-50/60'
                                            } ${grupos.length ? 'cursor-pointer hover:bg-[#7dd3fc]/10' : 'cursor-default'
                                            } ${eHoje ? 'ring-2 ring-inset ring-[#0ea5e9]' : ''}`}
                                    >
                                        <div className="mb-1 flex items-center justify-between">
                                            <span className={`text-xs font-semibold ${doMes ? 'text-gray-700' : 'text-gray-300'}`}>
                                                {dia.getDate()}
                                            </span>
                                            {grupos.length > 0 && (
                                                <span className="rounded-full bg-[#0f172a] px-1.5 py-0.5 text-[10px] font-semibold text-white">
                                                    {grupos.length}
                                                </span>
                                            )}
                                        </div>

                                        <div className="space-y-1">
                                            {grupos.slice(0, 3).map(g => (
                                                <div
                                                    key={g.group_broadcast_id}
                                                    className={`truncate rounded px-1.5 py-0.5 text-[10px] font-medium ${corDoStatus(g.status)}`}
                                                    title={g.title ?? undefined}
                                                >
                                                    {horaBRT(quandoAconteceu(g))} {g.title ?? 'Sem título'}
                                                </div>
                                            ))}
                                            {grupos.length > 3 && (
                                                <div className="px-1.5 text-[10px] font-medium text-gray-400">
                                                    +{grupos.length - 3} grupo(s)
                                                </div>
                                            )}
                                            {leads > 0 && (
                                                <div className="px-1.5 pt-0.5 text-[10px] text-gray-400">
                                                    {leads.toLocaleString('pt-BR')} leads
                                                </div>
                                            )}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>

            {diaAberto && (
                <BroadcastDayModal
                    dia={diaAberto}
                    grupos={gruposDoDiaAberto}
                    onClose={() => setDiaAberto(null)}
                    onDuplicate={onDuplicate}
                    onCancel={onCancel}
                    onView={onView}
                />
            )}
        </div>
    );
}

function Indicador({ rotulo, valor, detalhe, icone }: { rotulo: string; valor: string; detalhe?: string; icone?: React.ReactNode }) {
    return (
        <div className="rounded-xl border border-gray-100 bg-gray-50/60 px-3 py-2.5">
            <div className="flex items-center gap-1 text-[11px] font-medium uppercase tracking-wider text-gray-400">
                {icone}
                {rotulo}
            </div>
            <div className="mt-0.5 text-lg font-semibold text-gray-900">{valor}</div>
            {detalhe && <div className="text-[11px] text-gray-400">{detalhe}</div>}
        </div>
    );
}

const corDoStatus = (status?: string | null): string => {
    switch (status) {
        case 'completed':
        case 'sent': return 'bg-emerald-50 text-emerald-700';
        case 'sending':
        case 'processing': return 'bg-cyan-50 text-cyan-700';
        case 'partial': return 'bg-amber-50 text-amber-700';
        case 'failed': return 'bg-red-50 text-red-700';
        case 'cancelled': return 'bg-gray-100 text-gray-500';
        default: return 'bg-blue-50 text-blue-700';
    }
};

const horaBRT = (isoUtc?: string): string => {
    if (!isoUtc) return '';
    const d = new Date(isoUtc);
    if (isNaN(d.getTime())) return '';
    return new Intl.DateTimeFormat('pt-BR', {
        timeZone: BRT_TZ, hour: '2-digit', minute: '2-digit',
    }).format(d);
};
